import { browserSupportsWebAuthn, platformAuthenticatorIsAvailable, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { api } from './api';
import { deviceName } from './device';
import { isAndroid, isIOS } from './install';

// Passkeys: sign in with Face ID, Touch ID, a fingerprint or the screen lock. The server runs the
// WebAuthn ceremony (begin → the device signs → finish); this file drives the device side.

/** Can this device sign in with its own Face ID / Touch ID / fingerprint? */
export async function passkeysAvailable(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  return platformAuthenticatorIsAvailable().catch(() => false);
}

/** What to call it on this device. */
export function biometricName(): string {
  if (isIOS) return 'Face ID';
  // Android shows its own prompt: the fingerprint, or the face on phones with secure face unlock.
  if (isAndroid) return document.documentElement.lang === 'ja' ? '指紋または顔認証' : 'fingerprint or face';
  const ua = navigator.userAgent;
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Windows/.test(ua)) return 'Windows Hello';
  return 'your device';
}

/** Signs in with a passkey on this device; returns the session (token + user). */
export async function signInWithPasskey() {
  const begin = await api.passkeyLoginBegin();
  const answer = await startAuthentication({ optionsJSON: begin.options });
  return api.passkeyLoginFinish(begin.ceremony, answer);
}

/** Adds a passkey for this device to the signed-in account. */
export async function addPasskey() {
  const begin = await api.passkeyRegisterBegin();
  const answer = await startRegistration({ optionsJSON: begin.options });
  return api.passkeyRegisterFinish(begin.ceremony, deviceName(), answer);
}

// Face sign-in in the Android app. Phone makers like OnePlus keep face unlock for their own lock
// screen and don't offer it to apps, so passkeys (and any app prompt) get only the fingerprint.
// Instead the app keeps a phone key from the server (see devicekeys.go), sealed so it opens only
// while the phone is unlocked: the face unlock of the phone is the check. App 1.6.0 still asked
// for a biometric prompt; 1.7.0+ asks only if "Also ask for my fingerprint" is on.

type PhoneKey = { user: string; id: string; secret: string };
const app = () => (typeof window !== 'undefined' ? window.VBNative : undefined);

/** Is this the Android app with face or fingerprint unlock set up on the phone? */
export const phoneUnlockAvailable = () => !!app()?.faceUnlockAvailable?.();
/** App 1.7.0+: "" if the fingerprint option isn't available, else whether it's on. */
export const phoneKeyAsks = (): '' | 'on' | 'off' => (app()?.phoneKeyAsks?.() as '' | 'on' | 'off' | undefined) ?? '';
export const setPhoneKeyAsks = (on: boolean) => app()?.setPhoneKeyAsks?.(on);
/** App 1.7.0+: signing in needs only the phone to be unlocked (or the fingerprint, if chosen). */
export const phoneUnlockSignsIn = () => typeof app()?.phoneKeyAsks === 'function';
/** How this phone signs in: its own face unlock, or a fingerprint prompt. */
export const phoneSignInWith = (): 'face' | 'fingerprint' => (phoneKeyAsks() === 'on' ? 'fingerprint' : 'face');

/** The id of the phone key saved on this phone, or "". */
export const savedPhoneKey = (): string => {
  const raw = app()?.phoneKey?.() || '';
  try {
    return raw ? (JSON.parse(raw) as { id: string }).id : '';
  } catch {
    return '';
  }
};

/** Runs one native unlock prompt; resolves with its answer (the app replies with a "vbphonekey" event). */
function nativePrompt(start: () => void): Promise<{ ok: boolean; key?: string; error?: string | null }> {
  return new Promise((resolve) => {
    const on = (e: Event) => {
      removeEventListener('vbphonekey', on);
      resolve((e as CustomEvent).detail);
    };
    addEventListener('vbphonekey', on);
    start();
  });
}

/** Turns on face unlock for this phone: the server makes a key, the app keeps it after a face check. */
export async function turnOnPhoneUnlock(): Promise<boolean> {
  const key = await api.addPhoneKey(deviceName());
  const r = await nativePrompt(() => app()!.savePhoneKey!(JSON.stringify(key)));
  if (!r.ok) {
    api.deletePasskey(key.id).catch(() => undefined);
    if (r.error) throw new Error(r.error);
    return false;
  }
  // Earlier keys this app made that never worked (app 1.7.0 couldn't open them after face unlock).
  const stale = (await api.passkeys().catch(() => [])).filter((k) => k.kind === 'phone' && k.id !== key.id && k.name === deviceName() && !k.lastUsed);
  await Promise.all(stale.map((k) => api.deletePasskey(k.id).catch(() => undefined)));
  return true;
}

/** Forgets this phone's key (on the phone and on the server). */
export async function turnOffPhoneUnlock() {
  const id = savedPhoneKey();
  app()?.clearPhoneKey?.();
  if (id) await api.deletePasskey(id).catch(() => undefined);
}

/** Signs in with this phone's key after a face or fingerprint unlock; null if cancelled. */
export async function signInWithPhoneKey() {
  const r = await nativePrompt(() => app()!.unlockPhoneKey!());
  if (!r.ok || !r.key) {
    if (r.error) throw new Error(r.error);
    return null;
  }
  try {
    return await api.phoneKeyLogin(JSON.parse(r.key) as PhoneKey);
  } catch (e) {
    // Turned off from another device, or the password changed: this key is no good any more.
    app()?.clearPhoneKey?.();
    throw e;
  }
}

/** Turns the browser's errors into something people understand. */
export function passkeyError(e: unknown): string | null {
  const err = e as { name?: string; message?: string };
  if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return null; // cancelled
  if (err?.name === 'InvalidStateError') return 'This device already has Face ID / fingerprint sign-in for your account.';
  return err?.message || 'Something went wrong';
}
